import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaNewspaper, FaClock, FaUser, FaFire, FaTrophy, FaGamepad, FaCalendarAlt, FaExternalLinkAlt, FaHeart, FaComment, FaShare, FaSpinner, FaExclamationTriangle, FaTimes, FaPaperPlane, FaArrowDown } from 'react-icons/fa';
import { theme, animations } from '../../styles/theme';
import { useAuth } from '../../hooks/useApi';
import ApiService from '../../services/ApiService';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const NewsContainer = styled.div`
  min-height: 100vh;
  background: ${theme.gradients.background};
  position: relative;
  overflow: hidden;

&::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);

    
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${animations.sparkle} 4s ease-in-out infinite;

  }
`;

const NewsContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 1024px) {
    padding: ${theme.spacing.lg};
  }
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.sm};
  }
`;

const NewsHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xxl};
  animation: ${fadeIn} 0.8s ease-out;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin-bottom: ${theme.spacing.xl};
  }
  
  @media (max-width: 480px) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const NewsFilters = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xxl};
  overflow-x: auto;
  padding-bottom: ${theme.spacing.lg};
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
`;

const FilterButton = styled.button`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.backgroundLight};
  color: ${theme.colors.textSecondary};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.large};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-weight: ${theme.typography.fontWeights.medium};

  &:hover {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    background: rgba(255, 215, 0, 0.1);
  }

  &.active {
    background: ${theme.gradients.primary};
    color: #000;
    border-color: ${theme.colors.primary};
  }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xl};
  padding-left: ${theme.spacing.md};
  padding-right: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xxl};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainNews = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const FeaturedArticle = styled.article`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  overflow: hidden;
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  animation: ${slideIn} 0.8s ease-out;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.cardHover};
  }
`;

const ArticleImage = styled.div`
  width: 100%;
  height: 240px;
  background: ${theme.gradients.primary};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 215, 0, 0.8), rgba(255, 140, 0, 0.8));
  }

  &::after {
    content: '📰';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 4rem;
    opacity: 0.7;
  }
`;

const ArticleContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const ArticleCategory = styled.span`
  background: ${theme.colors.primary};
  color: #000;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.typography.fontSizes.xs};
  font-weight: ${theme.typography.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ArticleTitle = styled.h2`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.xl};
  margin: ${theme.spacing.md} 0;
  line-height: 1.3;
`;

const ArticleExcerpt = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.md};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.md};
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSizes.sm};
`;

const PublishDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.sm};
`;

const ArticleActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.cardBorder};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSizes.sm};
  transition: all 0.3s ease;

  &:hover {
    color: ${theme.colors.primary};
  }

  &.liked {
    color: #ff6b6b;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const SidebarCard = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.lg};
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.8s ease-out 0.4s both;
`;

const SidebarTitle = styled.h3`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.lg};
  margin: 0 0 ${theme.spacing.md} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const NewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const NewsItem = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.cardBorder};
  cursor: pointer;
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:hover {
    transform: translateX(5px);
  }
`;

const NewsItemImage = styled.div`
  width: 60px;
  height: 60px;
  background: ${theme.gradients.primary};
  border-radius: ${theme.borderRadius.medium};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const NewsItemContent = styled.div`
  flex: 1;
`;

const NewsItemTitle = styled.h4`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.sm};
  margin: 0 0 ${theme.spacing.xs} 0;
  line-height: 1.4;
`;

const NewsItemDate = styled.div`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.xs};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textSecondary};
  gap: ${theme.spacing.md};
`;

const LoadingSpinner = styled.div`
  color: ${theme.colors.primary};
  font-size: ${theme.typography.fontSizes.xl};
  animation: spin 1s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.lg} 0;
  color: ${theme.colors.error};
  text-align: center;
`;

const RetryButton = styled.button`
  background: ${theme.colors.error};
  color: white;
  border: none;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  margin-top: ${theme.spacing.md};
  transition: all 0.3s ease;

  &:hover {
    background: darkred;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.md};
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${theme.shadows.modal};
  animation: ${slideIn} 0.3s ease-out;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: ${theme.colors.error};
    transform: rotate(90deg);
  }
`;

const ModalImage = styled.div`
  width: 100%;
  height: 300px;
  background: ${theme.gradients.primary};
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing.xl};
`;

const CommentSection = styled.div`
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.cardBorder};
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const CommentItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  border: 1px solid ${theme.colors.cardBorder};
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
`;

const CommentAuthor = styled.span`
  color: ${theme.colors.primary};
  font-weight: bold;
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const TextArea = styled.textarea`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md};
  color: ${theme.colors.textPrimary};
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const SubmitButton = styled.button`
  background: ${theme.gradients.primary};
  color: #000;
  border: none;
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.medium};
  font-weight: bold;
  cursor: pointer;
  align-self: flex-end;
  transition: transform 0.2s;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  &:hover {
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.primary};
  color: #000;
  padding: ${theme.spacing.sm} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.large};
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  z-index: 2000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const RefreshIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${props => props.pullDistance}px;
  overflow: hidden;
  transition: height 0.1s ease-out;
  color: ${theme.colors.primary};
  font-size: 24px;
  width: 100%;
  opacity: ${props => Math.min(props.pullDistance / 100, 1)};
`;

const News = ({ onNavigate }) => {
  const { user, token } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [likedArticles, setLikedArticles] = useState(new Set());
  const [newsData, setNewsData] = useState([]);
  const [sidebarNews, setSidebarNews] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // New state for modal and comments
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isDesktopView, setIsDesktopView] = useState(window.innerWidth > 968);

  // Swipe to refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      setTouchStart(e.targetTouches[0].clientY);
    }
  };

  const handleTouchMove = (e) => {
    if (touchStart !== null && window.scrollY === 0) {
      const currentY = e.targetTouches[0].clientY;
      const diff = currentY - touchStart;
      if (diff > 0) {
        // Pulling down
        setPullDistance(diff > 150 ? 150 : diff);
      }
    }
  };

  const handleTouchEnd = () => {
    if (pullDistance > 100) {
      refreshNewsData();
    }
    setTouchStart(null);
    setPullDistance(0);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopView(window.innerWidth > 968);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load news data from Strapi
  useEffect(() => {
    const loadNewsData = async () => {
      if (!token) return;

      try {
        setLoading(true);
        setError(null);

        // Load main news articles, trending news, and events
        const [mainArticlesResponse, trendingNewsResponse, eventsResponse] = await Promise.all([
          ApiService.getNewsArticles(1, 20),
          ApiService.getTrendingNews(10),
          ApiService.getUpcomingEvents(10)
        ]);

        // Process main articles
        if (mainArticlesResponse?.data) {
          const processedArticles = mainArticlesResponse.data.map(article => {
            const attrs = article.attributes || article;
            return {
              id:  article.documentId || article.id,
              category: attrs.category || 'General',
              title: attrs.title || 'Untitled Article',
              excerpt: attrs.excerpt || attrs.content?.substring(0, 200) + '...' || 'No excerpt available',
              author: attrs.author?.data?.attributes?.username || attrs.author?.username || attrs.authorName || 'Anonymous',
              date: attrs.publishedAt ? formatDate(attrs.publishedAt) : formatDate(attrs.createdAt) || 'Recently',
              likes: attrs.likes || attrs.likeCount || 0,
              comments: attrs.comments?.data?.length || attrs.commentCount || 0,
              commentsList: attrs.comments?.data || [], // Store actual comments
              type: attrs.featured ? 'featured' : 'normal',
              content: attrs.content || '',
              image: attrs.image?.data?.attributes?.url || null,
              slug: attrs.slug || `article-${article.id}`,
              viewCount: attrs.viewCount || 0
            };
          });
          setNewsData(processedArticles);
        }

        // Process sidebar/trending news
        if (trendingNewsResponse?.data) {
          const processedTrending = trendingNewsResponse.data.map(item => {
            const attrs = item.attributes || item;
            return {
              id: item.id || item.documentId,
              title: attrs.title || 'Untitled',
              date: formatDate(attrs.createdAt) || 'Recently',
              icon: attrs.icon || '📰',
              category: attrs.category || 'General'
            };
          });
          setSidebarNews(processedTrending);
        }

        // Process upcoming events
        if (eventsResponse?.data) {
          const processedEvents = eventsResponse.data.map(event => {
            const attrs = event.attributes || event;
            return {
              id: event.id || event.documentId,
              title: attrs.title || 'Upcoming Event',
              description: attrs.description || '',
              date: formatDate(attrs.eventDate) || 'Soon',
              icon: attrs.icon || '🎯',
              eventType: attrs.eventType || 'General',
              maxParticipants: attrs.maxParticipants,
              prizePool: attrs.prizePool
            };
          });
          setUpcomingEvents(processedEvents);
        }

      } catch (error) {
        console.error('Failed to load news data:', error);
        setError('Unable to connect to news service. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    loadNewsData();
  }, [token]);

  // Debug effect to log user data and liked articles
  useEffect(() => {
    console.log('🔄 News component user/liked articles updated:', {
      hasUser: !!user,
      userId: user?.id,
      likedArticlesSize: likedArticles.size,
      userLikedNewsArticle: user?.liked_news_article?.length || 0,
      userLikedNewsArticleIds: user?.liked_news_article?.map(a => a.id || a.documentId) || []
    });
  }, [user, likedArticles]);

  // Load user's liked articles from server and user object
  useEffect(() => {
    const loadUserLikedArticles = async () => {
      if (!token || !user) return;
      
      try {
        let likedIds = [];
        // First, try to get liked articles from user object
        if (user.liked_news_article && Array.isArray(user.liked_news_article)) {
          likedIds = user.liked_news_article.map(article => 
            article.id || article.documentId
          );
        }
        
        // If user object doesn't have liked articles, try API
        if (likedIds.length === 0) {
          try {
            const userLikedResponse = await ApiService.getUserLikedArticles();
            
            let articles = [];
            if (Array.isArray(userLikedResponse)) {
              articles = userLikedResponse;
            } else if (userLikedResponse?.data && Array.isArray(userLikedResponse.data)) {
              articles = userLikedResponse.data;
            } else if (userLikedResponse?.liked_news_article) {
               articles = userLikedResponse.liked_news_article;
            }

            if (articles.length > 0) {
              likedIds = articles.map(article => 
                article.id || article.documentId
              );
            }
          } catch (apiError) {
            console.warn('Could not load user liked articles from API:', apiError);
          }
        }
        
        setLikedArticles(new Set(likedIds));
      } catch (error) {
        console.warn('Could not load user liked articles:', error);
        setLikedArticles(new Set());
      }
    };

    loadUserLikedArticles();
  }, [token, user]);

  // Update liked articles when user object changes
  useEffect(() => {
    if (user?.liked_news_article && Array.isArray(user.liked_news_article)) {
      const likedIds = user.liked_news_article.map(article => 
        article.id || article.documentId
      );
      setLikedArticles(new Set(likedIds));
    }
  }, [user?.liked_news_article]);

  // Helper function to check if an article is liked by the user
  const isArticleLiked = (articleId) => {
    if (!user || !articleId) return false;

    const articleIdStr = String(articleId);

    if (likedArticles.has(articleId) || likedArticles.has(articleIdStr)) {
      return true;
    }

    if (user.liked_news_article && Array.isArray(user.liked_news_article)) {
      const found = user.liked_news_article.some(likedArticle => {
        const likedId = likedArticle.id || likedArticle.documentId;
        const likedIdStr = String(likedId);
        return likedId === articleId || 
               likedIdStr === articleIdStr ||
               likedId === articleIdStr ||
               likedIdStr === articleId;
      });
      if (found) return true;
    }
    return false;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffTime / (1000 * 60));

      if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
      if (diffHours < 24) return `${diffHours} hours ago`;
      if (diffDays === 1) return '1 day ago';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  const filters = [
    { id: 'all', label: 'All', icon: FaNewspaper },
    { id: 'gaming', label: 'Gaming', icon: FaGamepad },
    { id: 'updates', label: 'Updates', icon: FaTrophy },
    { id: 'community', label: 'Community', icon: FaFire }
  ];

  const handleLike = async (articleId) => {
    if (!token) {
      console.error('No token available for like action');
      return;
    }

    try {
      const isCurrentlyLiked = isArticleLiked(articleId);
      const newLikedState = !isCurrentlyLiked;
      
      console.log('🔄 Handling like for article:', { 
        articleId, 
        currentlyLiked: isCurrentlyLiked, 
        newState: newLikedState,
        inSet: likedArticles.has(articleId),
        inUserData: user?.liked_news_article?.some(a => (a.id || a.documentId) === articleId)
      });
      
      // Call API with the new state (like/unlike)
      const response = await ApiService.likeNewsArticle(articleId, newLikedState);
      
      console.log('✅ Like API response:', response);
      
      // Update liked articles set based on server response
      if (response?.data) {
        setLikedArticles(prev => {
          const newSet = new Set(prev);
          if (response.data.liked || newLikedState) {
            newSet.add(articleId);
          } else {
            newSet.delete(articleId);
          }
          return newSet;
        });

        // Update the article's like count with server data
        setNewsData(prev => prev.map(article => 
          article.id === articleId 
            ? { 
                ...article, 
                likes: response.data.likes || response.data.likeCount || article.likes
              }
            : article
        ));
      }
      
      // Refresh article data from server to ensure consistency
      await refreshSingleArticle(articleId);
      
    } catch (error) {
      console.error('Failed to update like:', error);
      // Don't revert - just show error and let server state remain authoritative
    }
  };

  // Helper function to refresh a single article's data
  const refreshSingleArticle = async (articleId) => {
    try {
      const articleResponse = await ApiService.getNewsArticle(articleId);
      if (articleResponse?.data) {
        const attrs = articleResponse.data.attributes || articleResponse.data;
        const updatedArticle = {
          id: articleResponse.data.id || articleResponse.data.documentId,
          category: attrs.category || 'General',
          title: attrs.title || 'Untitled Article',
          excerpt: attrs.excerpt || attrs.content?.substring(0, 200) + '...' || 'No excerpt available',
          author: attrs.author?.data?.attributes?.username || attrs.author?.username || attrs.authorName || 'Anonymous',
          date: attrs.publishedAt ? formatDate(attrs.publishedAt) : formatDate(attrs.createdAt) || 'Recently',
          likes: attrs.likes || attrs.likeCount || 0,
          comments: attrs.comments?.data?.length || attrs.commentCount || 0,
          type: attrs.featured ? 'featured' : 'normal',
          content: attrs.content || '',
          image: attrs.image?.data?.attributes?.url || null,
          slug: attrs.slug || `article-${articleResponse.data.id}`,
          viewCount: attrs.viewCount || 0
        };

        // Update the specific article in the news data
        setNewsData(prev => prev.map(article => 
          article.id === articleId ? updatedArticle : article
        ));
      }
    } catch (error) {
      console.warn('Failed to refresh single article:', error);
    }
  };

  // Refresh all news data
  const refreshNewsData = async () => {
    if (!token || refreshing) return;
    
    try {
      setRefreshing(true);
      setError(null);

      const [mainArticlesResponse, trendingNewsResponse, eventsResponse] = await Promise.all([
        ApiService.getNewsArticles(1, 20),
        ApiService.getTrendingNews(10),
        ApiService.getUpcomingEvents(10)
      ]);

      // Process main articles
      if (mainArticlesResponse?.data) {
        const processedArticles = mainArticlesResponse.data.map(article => {
          const attrs = article.attributes || article;
          return {
            id: article.id || article.documentId,
            category: attrs.category || 'General',
            title: attrs.title || 'Untitled Article',
            excerpt: attrs.excerpt || attrs.content?.substring(0, 200) + '...' || 'No excerpt available',
            author: attrs.author?.data?.attributes?.username || attrs.author?.username || attrs.authorName || 'Anonymous',
            date: attrs.publishedAt ? formatDate(attrs.publishedAt) : formatDate(attrs.createdAt) || 'Recently',
            likes: attrs.likes || attrs.likeCount || 0,
            comments: attrs.comments?.data?.length || attrs.commentCount || 0,
            type: attrs.featured ? 'featured' : 'normal',
            content: attrs.content || '',
            image: attrs.image?.data?.attributes?.url || null,
            slug: attrs.slug || `article-${article.id}`,
            viewCount: attrs.viewCount || 0
          };
        });
        setNewsData(processedArticles);
      }

      // Process trending news
      if (trendingNewsResponse?.data) {
        setSidebarNews(trendingNewsResponse.data.map(item => {
          const attrs = item.attributes || item;
          return {
            id: item.id || item.documentId,
            title: attrs.title || 'Untitled',
            date: formatDate(attrs.createdAt) || 'Recently',
            icon: attrs.icon || '📰',
            category: attrs.category || 'General'
          };
        }));
      }

      // Process events
      if (eventsResponse?.data) {
        setUpcomingEvents(eventsResponse.data.map(event => {
          const attrs = event.attributes || event;
          return {
            id: event.id || event.documentId,
            title: attrs.title || 'Upcoming Event',
            description: attrs.description || '',
            date: formatDate(attrs.eventDate) || 'Soon',
            icon: attrs.icon || '🎯',
            eventType: attrs.eventType || 'General',
            maxParticipants: attrs.maxParticipants,
            prizePool: attrs.prizePool
          };
        }));
      }
    } catch (error) {
      console.error('Failed to refresh news:', error);
      setError('Failed to refresh news data. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  const retryLoading = async () => {
    setLoading(true);
    setError(null);
    await refreshNewsData();
    setLoading(false);
  };

  const handleShare = async (article) => {
    const shareData = {
      title: article.title,
      text: article.excerpt,
      url: window.location.origin + `/news/${article.slug}`
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Error sharing:', error);
        handleCopyLink(article);
      }
    } else {
      handleCopyLink(article);
    }
  };

  const handleCopyLink = (article) => {
    const url = window.location.origin + `/news/${article.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }).catch(err => {
      console.error('Failed to copy link:', err);
    });
  };

  // Handle article reading
  const handleReadMore = async (article) => {
    try {
      // Increment view count
      await ApiService.incrementArticleViews(article.id);
      
      // Update local state
      setNewsData(prev => prev.map(a => 
        a.id === article.id 
          ? { ...a, viewCount: (a.viewCount || 0) + 1 }
          : a
      ));
      
      // Navigate to full article (implement your navigation logic)
      console.log('Reading article:', article.slug);
      // Open modal
      setSelectedArticle(article);
      // Fetch fresh data including comments
      refreshSingleArticle(article.id);
    } catch (error) {
      console.error('Failed to track article view:', error);
    }
  };

  const handleComment = (article) => {
    setSelectedArticle(article);
    // Scroll to comments after modal opens (simple timeout for now)
    setTimeout(() => {
      const commentsSection = document.getElementById('comments-section');
      if (commentsSection) {
        commentsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  const handleEventClick = (event) => {
    console.log('Event clicked:', event.title);
    // Could open a modal for event details too
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !selectedArticle) return;

    setSubmittingComment(true);
    try {
      await ApiService.addNewsComment(selectedArticle.id, {
        content: commentText,
        userId: user.id
      });
      
      setCommentText('');
      await refreshSingleArticle(selectedArticle.id);
    } catch (error) {
      console.error('Failed to submit comment:', error);
      // Could show error toast
    } finally {
      setSubmittingComment(false);
    }
  };

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      refreshNewsData();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filteredNews = newsData.filter(article => {
    if (activeFilter === 'all') return true;
    return article.category.toLowerCase() === activeFilter;
  });

  if (loading) {
    return (
      <NewsContainer>
        <NewsContent>
          <LoadingContainer>
            <LoadingSpinner>
              <FaSpinner />
            </LoadingSpinner>
            <div>Loading latest news...</div>
          </LoadingContainer>
        </NewsContent>
      </NewsContainer>
    );
  }

  return (
    <NewsContainer
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <NewsContent>
        <RefreshIndicator pullDistance={pullDistance}>
          {pullDistance > 100 ? <FaSpinner className="fa-spin" /> : <FaArrowDown />}
        </RefreshIndicator>
        {error && (
          <ErrorContainer>
            <FaExclamationTriangle /> {error}
            <RetryButton onClick={retryLoading}>Retry</RetryButton>
          </ErrorContainer>
        )}
        
        <NewsHeader>
          {/* <NewsTitle>
            <FaNewspaper />
            Gaming News & Updates
            {refreshing && (
              <LoadingSpinner style={{ fontSize: '24px', marginLeft: '12px' }}>
                <FaSpinner />
              </LoadingSpinner>
            )}
          </NewsTitle>
          <NewsSubtitle>
            Stay updated with the latest gaming news, platform updates, and community highlights

          </NewsSubtitle> */}
        </NewsHeader>

        <NewsFilters>
          {filters.map(filter => {
            const IconComponent = filter.icon;
            return (
              <FilterButton
                key={filter.id}
                className={activeFilter === filter.id ? 'active' : ''}
                onClick={() => setActiveFilter(filter.id)}
              >
                <IconComponent />
                {filter.label}
              </FilterButton>
            );
          })}
        </NewsFilters>

       { isDesktopView ? 
        (        
          <NewsGrid>
            <MainNews>
              {filteredNews.length > 0 ? (
                filteredNews.map((article, index) => (
                  <FeaturedArticle key={article.id} style={{ animationDelay: `${index * 0.1}s` }}>
                    <ArticleImage />
                    <ArticleContent>
                      <ArticleCategory>{article.category}</ArticleCategory>
                      <ArticleTitle>{article.title}</ArticleTitle>
                      <ArticleExcerpt>{article.excerpt}</ArticleExcerpt>
                    
                    <ArticleMeta>
                      <AuthorInfo>
                        <FaUser />
                        {article.author}
                      </AuthorInfo>
                      <PublishDate>
                        <FaClock />
                        {article.date}
                      </PublishDate>
                    </ArticleMeta>

                    <ArticleActions>
                      <ActionButton
                        className={isArticleLiked(article.id) ? 'liked' : ''}
                        onClick={() => handleLike(article.id)}
                        title={isArticleLiked(article.id) ? "Unlike this article" : "Like this article"}
                      >
                        <FaHeart />
                        {article.likes}
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleComment(article)}
                        title="View comments"
                      >
                        <FaComment />
                        {article.comments}
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleShare(article)}
                        title="Share article"
                      >
                        <FaShare />
                        Share
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleReadMore(article)}
                        title="Read full article"
                      >
                        <FaExternalLinkAlt />
                        Read More
                      </ActionButton>
                    </ArticleActions>
                  </ArticleContent>
                </FeaturedArticle>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: theme.spacing.xxl,
                  color: theme.colors.textMuted
                }}>
                  <FaNewspaper style={{ fontSize: '3rem', marginBottom: theme.spacing.md, opacity: 0.5 }} />
                  <h3 style={{ margin: 0, marginBottom: theme.spacing.sm }}>No News Available</h3>
                  <p style={{ margin: 0 }}>
                    {error ? 'Unable to load news articles.' : 'Check back later for the latest updates!'}
                  </p>
                </div>
              )}
            </MainNews>
                  <Sidebar>
              <SidebarCard>
                <SidebarTitle>
                  <FaFire />
                  Trending Now
                </SidebarTitle>
                <NewsList>
                  {sidebarNews.map(item => (
                    <NewsItem key={item.id}>
                      <NewsItemImage>
                        {item.icon}
                      </NewsItemImage>
                      <NewsItemContent>
                        <NewsItemTitle>{item.title}</NewsItemTitle>
                        <NewsItemDate>{item.date}</NewsItemDate>
                      </NewsItemContent>
                    </NewsItem>
                  ))}
                </NewsList>
              </SidebarCard>

              <SidebarCard>
                <SidebarTitle>
                  <FaCalendarAlt />
                  Upcoming Events
                  {refreshing && <LoadingSpinner style={{ fontSize: '14px', marginLeft: '8px' }}><FaSpinner /></LoadingSpinner>}
                </SidebarTitle>
                <NewsList>
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map(event => (
                      <NewsItem key={event.id} onClick={() => handleEventClick(event)}>
                        <NewsItemImage>{event.icon}</NewsItemImage>
                        <NewsItemContent>
                          <NewsItemTitle>{event.title}</NewsItemTitle>
                          <NewsItemDate>{event.date}</NewsItemDate>
                          {event.prizePool && (
                            <div style={{ fontSize: '11px', color: theme.colors.primary, marginTop: '2px' }}>
                              Prize: {event.prizePool}
                            </div>
                          )}
                        </NewsItemContent>
                      </NewsItem>
                    ))
                  ) : (
                    <div style={{ 
                      color: theme.colors.textMuted, 
                      fontSize: theme.typography.fontSizes.sm,
                      textAlign: 'center',
                      padding: theme.spacing.md
                    }}>
                      No upcoming events
                    </div>
                  )}
                </NewsList>
              </SidebarCard>
            </Sidebar>

        
          </NewsGrid>
        ):
        (
          <NewsGrid>
            <Sidebar>
              <SidebarCard>
                <SidebarTitle>
                  <FaFire />
                  Trending Now
                </SidebarTitle>
                <NewsList>
                  {sidebarNews.map(item => (
                    <NewsItem key={item.id}>
                      <NewsItemImage>
                        {item.icon}
                      </NewsItemImage>
                      <NewsItemContent>
                        <NewsItemTitle>{item.title}</NewsItemTitle>
                        <NewsItemDate>{item.date}</NewsItemDate>
                      </NewsItemContent>
                    </NewsItem>
                  ))}
                </NewsList>
              </SidebarCard>

              <SidebarCard>
                <SidebarTitle>
                  <FaCalendarAlt />
                  Upcoming Events
                  {refreshing && <LoadingSpinner style={{ fontSize: '14px', marginLeft: '8px' }}><FaSpinner /></LoadingSpinner>}
                </SidebarTitle>
                <NewsList>
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map(event => (
                      <NewsItem key={event.id} onClick={() => handleEventClick(event)}>
                        <NewsItemImage>{event.icon}</NewsItemImage>
                        <NewsItemContent>
                          <NewsItemTitle>{event.title}</NewsItemTitle>
                          <NewsItemDate>{event.date}</NewsItemDate>
                          {event.prizePool && (
                            <div style={{ fontSize: '11px', color: theme.colors.primary, marginTop: '2px' }}>
                              Prize: {event.prizePool}
                            </div>
                          )}
                        </NewsItemContent>
                      </NewsItem>
                    ))
                  ) : (
                    <div style={{ 
                      color: theme.colors.textMuted, 
                      fontSize: theme.typography.fontSizes.sm,
                      textAlign: 'center',
                      padding: theme.spacing.md
                    }}>
                      No upcoming events
                    </div>
                  )}
                </NewsList>
              </SidebarCard>
            </Sidebar>

            <MainNews>
              {filteredNews.length > 0 ? (
                filteredNews.map((article, index) => (
                  <FeaturedArticle key={article.id} style={{ animationDelay: `${index * 0.1}s` }}>
                    <ArticleImage />
                    <ArticleContent>
                      <ArticleCategory>{article.category}</ArticleCategory>
                      <ArticleTitle>{article.title}</ArticleTitle>
                      <ArticleExcerpt>{article.excerpt}</ArticleExcerpt>
                    
                    <ArticleMeta>
                      <AuthorInfo>
                        <FaUser />
                        {article.author}
                      </AuthorInfo>
                      <PublishDate>
                        <FaClock />
                        {article.date}
                      </PublishDate>
                    </ArticleMeta>

                    <ArticleActions>
                      <ActionButton
                        className={isArticleLiked(article.id) ? 'liked' : ''}
                        onClick={() => handleLike(article.id)}
                        title={isArticleLiked(article.id) ? "Unlike this article" : "Like this article"}
                      >
                        <FaHeart />
                        {article.likes}
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleComment(article)}
                        title="View comments"
                      >
                        <FaComment />
                        {article.comments}
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleShare(article)}
                        title="Share article"
                      >
                        <FaShare />
                        Share
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleReadMore(article)}
                        title="Read full article"
                      >
                        <FaExternalLinkAlt />
                        Read More
                      </ActionButton>
                    </ArticleActions>
                  </ArticleContent>
                </FeaturedArticle>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: theme.spacing.xxl,
                  color: theme.colors.textMuted
                }}>
                  <FaNewspaper style={{ fontSize: '3rem', marginBottom: theme.spacing.md, opacity: 0.5 }} />
                  <h3 style={{ margin: 0, marginBottom: theme.spacing.sm }}>No News Available</h3>
                  <p style={{ margin: 0 }}>
                    {error ? 'Unable to load news articles.' : 'Check back later for the latest updates!'}
                  </p>
                </div>
              )}
            </MainNews>
          </NewsGrid>
        )
      }

      </NewsContent>

      {/* Article Modal */}
      {selectedArticle && (
        <ModalOverlay onClick={() => setSelectedArticle(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedArticle(null)}>
              <FaTimes />
            </CloseButton>
            <ModalImage>
              {/* Use article image if available, otherwise default gradient */}
              {selectedArticle.image && <img src={selectedArticle.image} alt={selectedArticle.title} />}
            </ModalImage>
            <ModalBody>
              <ArticleCategory>{selectedArticle.category}</ArticleCategory>
              <ArticleTitle>{selectedArticle.title}</ArticleTitle>
              <ArticleMeta>
                <AuthorInfo><FaUser /> {selectedArticle.author}</AuthorInfo>
                <PublishDate><FaClock /> {selectedArticle.date}</PublishDate>
              </ArticleMeta>
              
              <div style={{ lineHeight: '1.8', color: theme.colors.textSecondary, marginBottom: theme.spacing.xl }}>
                {selectedArticle.content || selectedArticle.excerpt}
              </div>

              <ArticleActions>
                <ActionButton 
                  className={isArticleLiked(selectedArticle.id) ? 'liked' : ''}
                  onClick={() => handleLike(selectedArticle.id)}
                >
                  <FaHeart /> {selectedArticle.likes} Likes
                </ActionButton>
                <ActionButton onClick={() => handleShare(selectedArticle)}>
                  <FaShare /> Share
                </ActionButton>
              </ArticleActions>

              <CommentSection id="comments-section">
                <h3 style={{ color: theme.colors.textPrimary, marginBottom: theme.spacing.lg }}>
                  Comments ({selectedArticle.comments})
                </h3>
                
                <CommentList>
                  {selectedArticle.commentsList && selectedArticle.commentsList.length > 0 ? (
                    selectedArticle.commentsList.map((comment, idx) => (
                      <CommentItem key={comment.id || idx}>
                        <CommentHeader>
                          <CommentAuthor>{comment.attributes?.user?.data?.attributes?.username || 'User'}</CommentAuthor>
                          <span>{formatDate(comment.attributes?.createdAt)}</span>
                        </CommentHeader>
                        <div style={{ color: theme.colors.textSecondary }}>
                          {comment.attributes?.content}
                        </div>
                      </CommentItem>
                    ))
                  ) : (
                    <div style={{ color: theme.colors.textMuted, fontStyle: 'italic' }}>
                      No comments yet. Be the first to share your thoughts!
                    </div>
                  )}
                </CommentList>

                <CommentForm onSubmit={handleSubmitComment}>
                  <TextArea 
                    placeholder="Write a comment..." 
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                  />
                  <SubmitButton type="submit" disabled={submittingComment || !commentText.trim()}>
                    {submittingComment ? <FaSpinner className="fa-spin" /> : <FaPaperPlane />}
                    Post Comment
                  </SubmitButton>
                </CommentForm>
              </CommentSection>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}

      {showToast && (
        <Toast>
          Link copied to clipboard!
        </Toast>
      )}
    </NewsContainer>
  );
};

export default News;